import React, { useState, useCallback, useMemo, memo } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { BringgInput, Button, Modal, Notification } from '@bringg/react-components';
import { BringgIcon, BringgFontIcons } from '@bringg/bringg-icons';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import { ClientSideConfig } from '../../stores/stores/types';

import styles from './create-new-preset.module.scss';

const ModalTitle = memo(({ text }: { text: string }) => {
	return <div className={styles.saveModalTitle}>{text}</div>;
});

interface Props {
	activePreset: ClientSideConfig;
	visible: boolean;
	onClose: () => void;
}

export const CreateNewPreset = observer(({ activePreset, visible, onClose }: Props) => {
	const { t } = useTranslation();
	const [presetName, setPresetName] = useState('');

	const handleCreateNewPreset = useCallback(async () => {
		try {
			const newPresetResponse = await activePreset.saveAsNewPreset(presetName);

			const newPreset: ClientSideConfig = getRootEnv()
				.dashboardSdk.sdk.v2.presetViews()
				.get(newPresetResponse.id) as ClientSideConfig;
			await newPreset.activatePresetForView(false);

			setPresetName('');
			onClose();
			Notification.success(t('PRESET_VIEWS.PRESET_SAVED', { name: presetName }));
		} catch (error) {
			Notification.error(t('PRESET_VIEWS.FAILED_CREATE_NEW_PRESET_VIEW'));
		}
	}, [t, presetName, activePreset]);

	const handleCloseModal = useCallback(() => {
		setPresetName('');
		onClose();
	}, [onClose]);

	const footer = useMemo(
		() => [
			<Button key="cancel" type="link" onClick={handleCloseModal}>
				{t('GLOBAL.CANCEL')}
			</Button>,
			<Button key="submit" type="primary" onClick={handleCreateNewPreset} disabled={!presetName.length}>
				{t('PRESET_VIEWS.SAVE_DISPLAY')}
			</Button>
		],
		[t, presetName, onClose, handleCreateNewPreset]
	);

	return (
		<Modal
			destroyOnClose
			visible={visible}
			title={<ModalTitle text={t('PRESET_VIEWS.SAVE_MODAL.TITLE')} />}
			onCancel={onClose}
			maskClosable={false}
			footer={footer}
			keyboard={false}
		>
			<BringgInput
				placeholder={t('PRESET_VIEWS.SAVE_MODAL.DISPLAY_NAME')}
				type="text"
				prefix={<BringgIcon iconName={BringgFontIcons.User} />}
				value={presetName}
				onChange={e => setPresetName(e.target.value)}
				className={styles.saveInputName}
				data-test-id="save-as-preset-display-name"
			/>
		</Modal>
	);
});
